import {
  Component, OnInit, OnDestroy, NgZone, ChangeDetectorRef
} from '@angular/core';
import { Subscription } from 'rxjs';
import { ChatStateService } from '../../store/chat-state.service';
import { ChatSession, SessionTokens } from '../../models/chat.models';

@Component({
  selector: 'app-chat-sessions',
  template: `
    <div class="xcs-panel">

      <!-- Cap bar -->
      <div class="xcs-cap-bar">
        <span class="xcs-cap-label">Sessions</span>
        <span class="xcs-cap-badge"
              title="Active sessions vs. your per-user cap. Delete old sessions to free slots.">
          Cap: {{ sessions.length }} / {{ sessionsMax }}
          <span class="xcs-cap-note">per user</span>
        </span>
      </div>

      <!-- Header -->
      <div class="xcs-header">
        <span class="xcs-title">Conversations</span>
        <div class="xcs-header-btns">
          <button class="xcs-icon-btn"
                  [class.xcs-icon-btn--spinning]="loadingSessions"
                  (click)="refresh()" title="Refresh sessions">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor"
                 stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"
                 width="13" height="13">
              <path d="M1.5 8A6.5 6.5 0 018 1.5a6.5 6.5 0 015.5 3"/>
              <path d="M14.5 8A6.5 6.5 0 018 14.5a6.5 6.5 0 01-5.5-3"/>
              <polyline points="11,1 14,4 11,7"/>
              <polyline points="5,9 2,12 5,15"/>
            </svg>
          </button>
          <button class="xcs-new-btn" (click)="chatState.createNewSession()" title="New chat">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor"
                 stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"
                 width="12" height="12">
              <path d="M11 2.5L13.5 5M2.5 13.5h2.5l7.5-7.5a1.768 1.768 0 000-2.5L12 3a1.768 1.768 0 00-2.5 0L2 11v2.5z"/>
            </svg>
            New
          </button>
        </div>
      </div>

      <!-- Skeletons -->
      <div class="xcs-skeletons" *ngIf="loadingSessions">
        <div class="xcs-skeleton" *ngFor="let i of [1,2,3,4]"></div>
      </div>

      <!-- Session list -->
      <div class="xcs-list" *ngIf="!loadingSessions">
        <div class="xcs-item"
             *ngFor="let session of sessions; trackBy: trackById"
             [class.xcs-item--active]="session.id === activeSessionId"
             (click)="chatState.selectSession(session.id)">

          <!-- Rename input -->
          <ng-container *ngIf="editingId === session.id; else showTitle">
            <input class="xcs-rename" [value]="session.title" #ri
                   (keydown.enter)="saveRename(session.id, ri.value)"
                   (keydown.escape)="editingId = null"
                   (blur)="saveRename(session.id, ri.value)"
                   (click)="$event.stopPropagation()" autofocus/>
          </ng-container>

          <ng-template #showTitle>
            <!-- Title row -->
            <div class="xcs-item-body">
              <svg class="xcs-icon" viewBox="0 0 16 16" fill="currentColor" width="12" height="12">
                <path fill-rule="evenodd"
                  d="M2.5 2A1.5 1.5 0 001 3.5v7A1.5 1.5 0 002.5 12H4v2.25a.75.75 0 001.197.6L8.2 12H13.5A1.5 1.5 0 0015 10.5v-7A1.5 1.5 0 0013.5 2h-11z"
                  clip-rule="evenodd"/>
              </svg>
              <span class="xcs-item-title">{{ session.title }}</span>
            </div>

            <!-- Meta row -->
            <div class="xcs-meta">
              <span class="xcs-meta-msgs">
                <svg viewBox="0 0 12 12" fill="currentColor" width="9" height="9" style="opacity:.6">
                  <path d="M1 2.5A1.5 1.5 0 012.5 1h7A1.5 1.5 0 0111 2.5v5A1.5 1.5 0 019.5 9H8v1.5a.5.5 0 01-.8.4L5.5 9H2.5A1.5 1.5 0 011 7.5v-5z"/>
                </svg>
                {{ session.messageCount || 0 }}
              </span>

              <!-- Token pill — tooltip is a fixed portal to escape overflow clipping -->
              <span class="xcs-tok-pill"
                    *ngIf="session.tokens?.total"
                    [class.xcs-tok-pill--warn]="(session.tokens?.total || 0) > 500000"
                    (mouseenter)="showTokTooltip(session, $event)"
                    (mouseleave)="hideTokTooltip()">
                <svg viewBox="0 0 12 12" fill="none" stroke="currentColor"
                     stroke-width="1.4" width="8" height="8">
                  <circle cx="6" cy="6" r="5"/>
                  <path d="M6 4v2l1.5 1.5"/>
                </svg>
                {{ formatTok(session.tokens?.total || 0) }} tok
              </span>

              <!-- Time text — hidden (but space preserved) when actions are visible -->
              <span class="xcs-meta-time">{{ relativeTime(session.updatedAt || session.createdAt) }}</span>
            </div>
          </ng-template>

          <!-- Hover action buttons — positioned at bottom-right (meta-row level) -->
          <div class="xcs-item-actions" (click)="$event.stopPropagation()">
            <button class="xcs-act xcs-act--compact"
                    title="Compact — folds history into a summary, freeing context tokens"
                    (click)="doCompact(session.id)">
              <svg viewBox="0 0 14 14" fill="none" stroke="currentColor"
                   stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"
                   width="11" height="11">
                <rect x="1" y="3" width="12" height="8" rx="1.5"/>
                <line x1="4" y1="6" x2="10" y2="6"/>
                <line x1="5.5" y1="8.5" x2="8.5" y2="8.5"/>
              </svg>
            </button>
            <button class="xcs-act" title="Rename" (click)="editingId = session.id">
              <svg viewBox="0 0 14 14" fill="currentColor" width="10" height="10">
                <path d="M10.13 1.427a1.75 1.75 0 012.474 0l.97.97a1.75 1.75 0 010 2.474l-7.67 7.67A2.25 2.25 0 014.31 13H2.25A1.25 1.25 0 011 11.75V9.69a2.25 2.25 0 01.659-1.591l7.47-7.672zm.176 4.823L8.87 4.81 2.72 10.963v1.287h1.287l6.3-6.3z"/>
              </svg>
            </button>
            <button class="xcs-act xcs-act--del" title="Delete" (click)="doDelete(session.id)">
              <svg viewBox="0 0 14 14" fill="currentColor" width="10" height="10">
                <path fill-rule="evenodd" d="M5 3.25V4H2.75a.75.75 0 000 1.5h.3l.815 8.15A1.5 1.5 0 005.357 15h5.285a1.5 1.5 0 001.493-1.35l.815-8.15h.3a.75.75 0 000-1.5H11v-.75A2.25 2.25 0 008.75 1h-1.5A2.25 2.25 0 005 3.25zm2.25-.75a.75.75 0 00-.75.75V4h3v-.75a.75.75 0 00-.75-.75h-1.5z" clip-rule="evenodd"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- Empty state -->
        <div class="xcs-empty" *ngIf="sessions.length === 0">
          <svg viewBox="0 0 40 40" fill="none" stroke="currentColor" stroke-width="1.3" width="36" height="36">
            <rect x="4" y="4" width="32" height="26" rx="4"/>
            <path d="M14 36l6-6 6 6"/>
            <line x1="10" y1="13" x2="30" y2="13"/>
            <line x1="10" y1="19" x2="24" y2="19"/>
          </svg>
          <p>No conversations yet</p>
          <p class="xcs-empty-sub">Ask your first question below</p>
        </div>
      </div>

      <!-- Fixed-position token tooltip portal
           Rendered here (panel root) but positioned via position:fixed so it escapes
           the overflow:auto scroll container and appears to the RIGHT of the sidebar -->
      <div class="xcs-tok-portal"
           *ngIf="tooltipSession"
           [style.top.px]="tooltipPos.y"
           [style.left.px]="tooltipPos.x"
           (mouseenter)="keepTooltip()"
           (mouseleave)="leavePortal()">
        <div class="xcs-tok-portal-arrow"></div>
        <strong class="xcs-tok-portal-title">Token usage</strong>
        <div class="xcs-tok-portal-row">
          <span>Input</span>
          <strong>{{ (tooltipSession.tokens?.input || 0) | number }}</strong>
        </div>
        <div class="xcs-tok-portal-row">
          <span>Output</span>
          <strong>{{ (tooltipSession.tokens?.output || 0) | number }}</strong>
        </div>
        <div class="xcs-tok-portal-row">
          <span>Total</span>
          <strong>{{ (tooltipSession.tokens?.total || 0) | number }}</strong>
        </div>
        <div class="xcs-tok-portal-row">
          <span>Turns</span>
          <strong>{{ tooltipSession.tokens?.turns || 0 }}</strong>
        </div>
        <div class="xcs-tok-portal-row xcs-tok-portal-reset" *ngIf="tooltipSession.tokens?.resetAt">
          <span>Reset</span>
          <strong>{{ tooltipSession.tokens?.resetAt | date:'MMM d, h:mm a' }}</strong>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .xcs-panel {
      width: 230px; height: 100%;
      background: #fff; border-right: 1px solid #e8edf5;
      display: flex; flex-direction: column; overflow: hidden;
    }

    /* Cap bar */
    .xcs-cap-bar {
      display: flex; align-items: center; justify-content: space-between;
      padding: 7px 12px 6px; background: #f8fafc;
      border-bottom: 1px solid #e8edf5; flex-shrink: 0;
    }
    .xcs-cap-label { font-size: 10px; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: .07em; }
    .xcs-cap-badge {
      display: flex; align-items: center; gap: 4px;
      background: #eef2ff; border: 1px solid #c7d2fe;
      border-radius: 999px; padding: 2px 8px;
      font-size: 10.5px; font-weight: 600; color: #4f46e5; cursor: default;
    }
    .xcs-cap-note { font-weight: 400; color: #818cf8; font-size: 9.5px; }

    /* Header */
    .xcs-header {
      padding: 10px 12px 8px; border-bottom: 1px solid #e8edf5;
      display: flex; align-items: center; justify-content: space-between; flex-shrink: 0;
    }
    .xcs-title { font-size: 10.5px; font-weight: 700; color: #8b96af; text-transform: uppercase; letter-spacing: .08em; }
    .xcs-header-btns { display: flex; align-items: center; gap: 5px; }
    .xcs-icon-btn {
      background: none; border: none; cursor: pointer; color: #9ca3af;
      padding: 5px; border-radius: 7px; display: flex; align-items: center; justify-content: center;
      transition: color .13s, background .13s;
    }
    .xcs-icon-btn:hover { color: #6366f1; background: #eef2ff; }
    .xcs-icon-btn--spinning svg { animation: xcSpin 0.7s linear infinite; }
    @keyframes xcSpin { to { transform: rotate(360deg); } }
    .xcs-new-btn {
      display: flex; align-items: center; gap: 5px;
      background: linear-gradient(135deg,#6366f1,#4f7ef7);
      color: #fff; border: none; border-radius: 8px;
      padding: 5px 11px; font-size: 11px; font-weight: 600;
      cursor: pointer; transition: opacity .14s, transform .12s;
    }
    .xcs-new-btn:hover { opacity: .88; transform: scale(1.03); }

    /* List */
    .xcs-list { flex: 1; overflow-y: auto; padding: 5px; }
    .xcs-list::-webkit-scrollbar { width: 4px; }
    .xcs-list::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 99px; }

    /* Item */
    .xcs-item {
      padding: 8px 9px; border-radius: 10px;
      cursor: pointer; position: relative; margin-bottom: 2px;
      transition: background .12s;
    }
    .xcs-item:hover { background: #f4f6fc; }
    .xcs-item--active { background: #eef2ff; }
    .xcs-item--active .xcs-icon { color: #6366f1; }

    /* Show actions, hide time text (visibility keeps layout space so row doesn't collapse) */
    .xcs-item:hover .xcs-item-actions  { opacity: 1; pointer-events: auto; }
    .xcs-item:hover .xcs-meta-time     { visibility: hidden; }

    .xcs-item-body { display: flex; align-items: center; gap: 6px; }
    .xcs-icon { color: #a0aec0; flex-shrink: 0; transition: color .12s; }
    .xcs-item-title {
      font-size: 12.5px; font-weight: 500; color: #2d3a5e;
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    }

    /* Meta row */
    .xcs-meta {
      display: flex; align-items: center; gap: 5px;
      margin-top: 3px; padding-left: 18px;
      flex-wrap: nowrap; overflow: hidden;
    }
    .xcs-meta-msgs {
      display: flex; align-items: center; gap: 3px;
      font-size: 10px; color: #b0bac9; flex-shrink: 0;
    }
    .xcs-meta-time {
      font-size: 10px; color: #b0bac9;
      white-space: nowrap; margin-left: auto; flex-shrink: 0;
    }

    /* Token pill */
    .xcs-tok-pill {
      display: inline-flex; align-items: center; gap: 3px;
      background: #f0f4fc; border: 1px solid #d0d8f0;
      border-radius: 999px; padding: 1px 6px;
      font-size: 9.5px; font-weight: 600; color: #6366f1;
      cursor: default; flex-shrink: 0; white-space: nowrap;
      transition: background .12s, border-color .12s;
    }
    .xcs-tok-pill:hover { background: #eef2ff; border-color: #a5b4fc; }
    .xcs-tok-pill--warn { background: #fff7ed; border-color: #fed7aa; color: #c2410c; }
    .xcs-tok-pill--warn:hover { background: #ffedd5; }

    /* Fixed-position tooltip portal — escapes all overflow containers */
    .xcs-tok-portal {
      position: fixed;
      z-index: 999999;
      background: #1e2d50; color: #f1f5f9;
      border-radius: 10px; padding: 10px 14px;
      font-size: 11.5px;
      box-shadow: 0 6px 24px rgba(0,0,0,.32);
      min-width: 170px;
      display: flex; flex-direction: column; gap: 5px;
      transform: translateY(-50%);
      animation: xcTipIn .12s ease;
      pointer-events: auto;
    }
    @keyframes xcTipIn {
      from { opacity:0; transform:translateY(-50%) translateX(-5px); }
      to   { opacity:1; transform:translateY(-50%) translateX(0); }
    }
    /* Left-pointing arrow */
    .xcs-tok-portal-arrow {
      position: absolute;
      right: 100%; top: 50%; transform: translateY(-50%);
      border: 5px solid transparent; border-right-color: #1e2d50;
    }
    .xcs-tok-portal-title {
      font-size: 9.5px; font-weight: 700; color: #64748b;
      text-transform: uppercase; letter-spacing: .06em;
      padding-bottom: 5px; border-bottom: 1px solid rgba(255,255,255,.1);
      display: block;
    }
    .xcs-tok-portal-row {
      display: flex; justify-content: space-between; gap: 20px;
    }
    .xcs-tok-portal-row span  { color: #94a3b8; }
    .xcs-tok-portal-row strong { font-variant-numeric: tabular-nums; }
    .xcs-tok-portal-reset {
      margin-top: 2px; padding-top: 5px;
      border-top: 1px solid rgba(255,255,255,.1);
    }
    .xcs-tok-portal-reset span, .xcs-tok-portal-reset strong { font-size: 10px; color: #64748b; }

    /* Action buttons — at BOTTOM-RIGHT (meta-row level), NOT vertically centered */
    .xcs-item-actions {
      position: absolute;
      right: 4px;
      bottom: 5px;
      display: flex; gap: 1px;
      opacity: 0; pointer-events: none;
      background: rgba(240,244,252,.97);
      border-radius: 7px; padding: 2px;
      backdrop-filter: blur(4px);
      transition: opacity .15s;
    }
    .xcs-act {
      background: none; border: none; cursor: pointer;
      padding: 3px 4px; color: #9ca3af; border-radius: 5px;
      transition: color .12s, background .12s;
    }
    .xcs-act:hover          { color: #6366f1; background: #e8edf5; }
    .xcs-act--compact:hover { color: #0891b2; background: #ecfeff; }
    .xcs-act--del:hover     { color: #dc2626; background: #fff1f0; }

    /* Rename input */
    .xcs-rename {
      width: 100%; border: 1.5px solid #6366f1; border-radius: 7px;
      padding: 4px 7px; font-size: 12.5px; outline: none;
      color: #1e2d50; background: #fff;
    }

    /* Skeletons */
    .xcs-skeletons { padding: 8px 6px; display: flex; flex-direction: column; gap: 5px; }
    .xcs-skeleton {
      height: 50px; border-radius: 10px;
      background: linear-gradient(90deg,#f0f4fc 25%,#e8edf5 50%,#f0f4fc 75%);
      background-size: 200% 100%; animation: xcShimmer 1.5s infinite;
    }
    @keyframes xcShimmer { to { background-position: -200% 0; } }

    /* Empty state */
    .xcs-empty {
      display: flex; flex-direction: column; align-items: center; gap: 6px;
      padding: 48px 16px; color: #c0cad8; text-align: center;
    }
    .xcs-empty p { font-size: 12px; font-weight: 500; margin: 0; color: #b0bac9; }
    .xcs-empty-sub { font-size: 11px !important; color: #c0cad8 !important; }
  `]
})
export class ChatSessionsComponent implements OnInit, OnDestroy {
  sessions:        ChatSession[] = [];
  activeSessionId: string | null = null;
  loadingSessions  = false;
  sessionsMax      = 50;
  editingId:       string | null = null;

  // Fixed-position token tooltip
  tooltipSession: ChatSession | null = null;
  tooltipPos      = { x: 0, y: 0 };
  private tooltipHeld = false;

  private sub!: Subscription;

  constructor(
    public chatState: ChatStateService,
    private cdr: ChangeDetectorRef,
    private zone: NgZone
  ) {}

  ngOnInit(): void {
    this.sub = this.chatState.state$.subscribe(s => {
      this.zone.run(() => {
        this.sessions        = [...(s.sessions || [])];
        this.activeSessionId = s.activeSessionId;
        this.loadingSessions = s.isLoadingSessions;
        this.sessionsMax     = s.sessionsMax || 50;
        this.cdr.detectChanges();
      });
    });
  }

  ngOnDestroy(): void { this.sub?.unsubscribe(); }

  trackById(_: number, s: ChatSession): string { return s.id; }

  refresh(): void { this.chatState.loadSessions(); }

  saveRename(id: string, title: string): void {
    if (title?.trim()) this.chatState.renameSession(id, title.trim());
    this.editingId = null;
  }

  doDelete(id: string): void {
    if (confirm('Delete this conversation? This cannot be undone.')) {
      this.chatState.deleteSession(id);
    }
  }

  doCompact(id: string): void {
    if (confirm('Compact this session? History will be summarised into a single message to free up context tokens.')) {
      this.chatState.compactSession(id);
    }
  }

  // ── Token tooltip portal ──────────────────────────────────────────────────

  showTokTooltip(session: ChatSession, event: MouseEvent): void {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    // Tooltip appears to the RIGHT of the pill, vertically centred on it
    this.tooltipPos     = { x: rect.right + 10, y: rect.top + rect.height / 2 };
    this.tooltipSession = session;
    this.cdr.detectChanges();
  }

  hideTokTooltip(): void {
    setTimeout(() => {
      if (!this.tooltipHeld) {
        this.tooltipSession = null;
        this.cdr.detectChanges();
      }
    }, 80);
  }

  keepTooltip():  void { this.tooltipHeld = true; }

  leavePortal(): void {
    this.tooltipHeld    = false;
    this.tooltipSession = null;
    this.cdr.detectChanges();
  }

  // ── Formatters ────────────────────────────────────────────────────────────

  formatTok(n: number): string {
    if (!n || n < 0) return '0';
    if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
    if (n >= 1_000)     return Math.round(n / 1_000) + 'k';
    return String(n);
  }

  buildTokTip(t?: SessionTokens | null): string {
    if (!t) return '';
    return `in ${t.input?.toLocaleString()} · out ${t.output?.toLocaleString()} · ${t.turns} turns`;
  }

  relativeTime(iso: string | undefined): string {
    if (!iso) return '';
    const then = new Date(iso).getTime();
    if (isNaN(then)) return '';
    const secs  = Math.floor((Date.now() - then) / 1000);
    const mins  = Math.floor(secs / 60);
    const hours = Math.floor(mins / 60);
    const days  = Math.floor(hours / 24);
    if (secs  < 10) return 'Just now';
    if (secs  < 60) return `${secs}s ago`;
    if (mins  < 60) return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days  <  7) return `${days}d ago`;
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
}